import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import TaskCard from "@/components/TaskCard";
import TaskDialog from "@/components/TaskDialog";
import CompleteLinksDialog from "@/components/CompleteLinksDialog";
import KanbanView from "@/components/KanbanView";
import ListView from "@/components/ListView";
import CalendarView from "@/components/CalendarView";
import WeeklyGridView from "@/components/WeeklyGridView";
import Avatar from "@/components/Avatar";
import TeamStatusStrip from "@/components/TeamStatusStrip";
import { Button } from "@/components/ui/button";
import { Plus, LayoutGrid, RefreshCw, Columns3, CalendarDays, Rows3, CalendarClock, CalendarRange, AlertTriangle, Search, X, Grid3x3, Coffee, Download, List } from "lucide-react";
import { TID } from "@/constants/testIds";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "sonner";
import { priorityRank, STATUS_META } from "@/constants/status";

const VIEWS = [
  { key: "board", label: "By Editor", icon: Rows3 },
  { key: "list", label: "List", icon: List },
  { key: "kanban", label: "Kanban", icon: Columns3 },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "weekly-grid", label: "Weekly Grid", icon: Grid3x3 },
];

// Buckets used to lay out each member's tasks on the whiteboard
const BUCKETS = [
  { key: "today", label: "Today", icon: CalendarClock, tone: "text-[#E11D2E] border-[#E11D2E]/40" },
  { key: "tomorrow", label: "Tomorrow", icon: CalendarDays, tone: "text-amber-300 border-amber-500/30" },
  { key: "week", label: "This Week", icon: CalendarRange, tone: "text-sky-300 border-sky-500/30" },
  { key: "pending", label: "Still Pending", icon: AlertTriangle, tone: "text-rose-300 border-rose-500/30" },
];

/** Bucket a task into today / tomorrow / this-week / pending (backlog) — complete tasks are excluded.
 *  Returns an ARRAY of buckets so a task whose `start_date → deadline` span
 *  covers multiple days shows up in every column that intersects (Rishi's
 *  "Today is empty" screenshot bug — a task starting today, due tomorrow
 *  used to only appear in Tomorrow). */
function bucketsOf(task, now = new Date(), ownerId = null) {
  let effectiveStatus = task.status;
  if (ownerId && (task.assignees || []).length > 1) {
    const slot = (task.assignee_details || []).find((a) => a.user_id === ownerId);
    if (slot && slot.status) effectiveStatus = slot.status;
  }
  if (effectiveStatus === "complete") return [];

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTomorrow = new Date(startOfToday.getTime() + 86400000);
  const startOfDayAfterTomorrow = new Date(startOfToday.getTime() + 2 * 86400000);
  const dow = startOfToday.getDay();
  const daysToSunday = dow === 0 ? 0 : 7 - dow;
  const endOfWeek = new Date(startOfToday.getTime() + (daysToSunday + 1) * 86400000 - 1);

  const rawDeadline = task.deadline ? new Date(task.deadline) : null;
  const rawStart = task.start_date ? new Date(task.start_date) : null;
  const validDeadline = rawDeadline && !isNaN(rawDeadline.getTime()) ? rawDeadline : null;
  const validStart = rawStart && !isNaN(rawStart.getTime()) ? rawStart : null;

  // No dates at all → backlog.
  if (!validDeadline && !validStart) return ["pending"];
  // Treat missing start_date as = deadline (single-day task).
  const spanEnd = validDeadline || validStart;
  const spanStart = validStart || validDeadline;

  // Overdue — every day of the span is in the past → still-pending.
  if (spanEnd < startOfToday) return ["pending"];

  const buckets = new Set();
  // "Today" if the span covers today (start <= today && end >= today).
  if (spanStart < startOfTomorrow && spanEnd >= startOfToday) buckets.add("today");
  // "Tomorrow" if the span covers tomorrow.
  if (spanStart < startOfDayAfterTomorrow && spanEnd >= startOfTomorrow) buckets.add("tomorrow");
  // "This week" if any part of the span falls between day-after-tomorrow and end-of-week.
  if (spanStart <= endOfWeek && spanEnd >= startOfDayAfterTomorrow) buckets.add("week");
  // "Pending" if the deadline pushes past this week (future backlog).
  if (spanEnd > endOfWeek) buckets.add("pending");

  return buckets.size ? [...buckets] : ["pending"];
}

export default function Whiteboard() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [view, setViewState] = useState(() => localStorage.getItem("lim-wb-view") || "board");
  const setView = (v) => { setViewState(v); try { localStorage.setItem("lim-wb-view", v); } catch { /* ignore */ } };
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [defaultAssignee, setDefaultAssignee] = useState(null);
  const [prefillDate, setPrefillDate] = useState(null);
  const [loading, setLoading] = useState(true);
  // { by_date: {YYYY-MM-DD: [userId, ...]}, by_user: {userId: [dates]} }
  const [leaves, setLeaves] = useState({ by_date: {}, by_user: {} });
  // Post-completion popup — after a task flips to `complete`, we ask the user
  // (optionally) to attach the NAS workfile + final video/design link.
  const [completePopupTask, setCompletePopupTask] = useState(null);
  const todayIso = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  const load = useCallback(async (silent = false) => {
    if (silent !== true) setLoading(true);
    try {
      const [u, t] = await Promise.all([api.get("/users"), api.get("/tasks")]);
      setUsers(u.data);
      setTasks(t.data);
    } catch (e) {
      toast.error("Failed to load whiteboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Pull on-leave map for the visible window (± 45 days around today covers the
  // Weekly Grid, By-Editor "today"-column and the Calendar month view). Refresh
  // every 5 minutes so leaves filed mid-day surface on other people's boards.
  useEffect(() => {
    let alive = true;
    const fetchLeaves = async () => {
      const today = new Date();
      const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const from = new Date(today); from.setDate(from.getDate() - 45);
      const to = new Date(today); to.setDate(to.getDate() + 45);
      try {
        const { data } = await api.get("/daily-logs/leaves", { params: { date_from: iso(from), date_to: iso(to) } });
        if (alive) setLeaves({ by_date: data.by_date || {}, by_user: data.by_user || {} });
      } catch { /* keep last-known map */ }
    };
    fetchLeaves();
    const id = setInterval(fetchLeaves, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // NOTE: `tasksByUser` + `bucketedByUser` used to live here, but they read from the
  // raw `tasks` list and completely bypassed the filter bar — so the status/member
  // filters had no effect on the "By Editor" view. They're now derived AFTER
  // `visibleTasks` below so every view honours the same filters.

  const openNew = (assigneeId) => { setEditing(null); setDefaultAssignee(assigneeId); setDialogOpen(true); };
  const openEdit = (task) => { setEditing(task); setDefaultAssignee(null); setDialogOpen(true); };

  const togglePriority = async (task, level) => {
    const lvl = level || (task.priority ? "normal" : "urgent");
    const flag = lvl === "urgent" || lvl === "high";
    try {
      await api.patch(`/tasks/${task.id}`, { priority_level: lvl });
      setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, priority: flag, priority_level: lvl } : t));
      toast.success(`Priority set to ${lvl}`);
    } catch {
      toast.error("Failed to update priority");
    }
  };

  const delTask = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      toast.success("Task deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const updateStatus = async (task, newStatus, { hold_reason = "", incomplete_reason = "" } = {}) => {
    try {
      const payload = { status: newStatus };
      if (newStatus === "on_hold") payload.hold_reason = hold_reason;
      if (newStatus === "incomplete") payload.incomplete_reason = incomplete_reason;
      if (newStatus !== "on_hold" && task.hold_reason) payload.hold_reason = "";
      if (newStatus !== "incomplete" && task.incomplete_reason) payload.incomplete_reason = "";
      const { data } = await api.patch(`/tasks/${task.id}`, payload);
      setTasks((prev) => prev.map((x) => (x.id === task.id ? data : x)));
      toast.success(`“${task.title}” → ${STATUS_META[newStatus]?.label || newStatus}`);
      // If the task just flipped to Complete or Carry-forward → pop the
      // wrap-up dialog to log today's hours. Carry-forward hides the
      // delivery-links section (nothing shipped yet).
      const nowFinishing = ["complete", "carry_forward"].includes(newStatus);
      if (nowFinishing && task.status !== newStatus) {
        setCompletePopupTask({ ...data, __popup_mode: newStatus });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Status update failed");
    }
  };

  // ---- Drag & drop between bucket columns (Today / Tomorrow / This Week / Still Pending) ----
  const [dragging, setDragging] = useState(null); // { taskId, ownerId }
  const [dragTarget, setDragTarget] = useState(null); // "${ownerId}:${bucket}"

  const deadlineForBucket = (bucketKey) => {
    const now = new Date();
    const at18 = (base) => new Date(base.getFullYear(), base.getMonth(), base.getDate(), 18, 0, 0);
    if (bucketKey === "today") return at18(now).toISOString();
    if (bucketKey === "tomorrow") {
      const t = new Date(now.getTime() + 86400000);
      return at18(t).toISOString();
    }
    if (bucketKey === "week") {
      // Set to Sunday 18:00
      const dow = now.getDay();
      const daysToSunday = dow === 0 ? 0 : 7 - dow;
      const s = new Date(now.getTime() + daysToSunday * 86400000);
      return at18(s).toISOString();
    }
    // "pending" → clear the deadline so the task lands in the backlog
    return "";
  };

  const canMoveTask = (_task) => isManager;

  const onCardDragStart = (e, task) => {
    if (!canMoveTask(task)) { e.preventDefault(); return; }
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/task-id", task.id);
    setDragging({ taskId: task.id, ownerId: task.assigned_to });
  };
  const onCardDragEnd = () => { setDragging(null); setDragTarget(null); };

  const onBucketDragOver = (e, ownerId, bucketKey) => {
    if (!dragging || dragging.ownerId !== ownerId) return; // only allow within same member row
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragTarget(`${ownerId}:${bucketKey}`);
  };

  const onBucketDrop = async (e, ownerId, bucketKey) => {
    e.preventDefault();
    setDragTarget(null);
    if (!dragging) return;
    const taskId = dragging.taskId;
    setDragging(null);
    if (dragging.ownerId !== ownerId) return; // ignore cross-member drops
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    if (!canMoveTask(task)) {
      toast.error("Only managers can reschedule tasks.");
      return;
    }
    const newDeadline = deadlineForBucket(bucketKey);
    if ((task.deadline || "") === newDeadline) return;
    try {
      await api.patch(`/tasks/${taskId}`, { deadline: newDeadline });
      // Optimistic local update so the card jumps buckets immediately
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, deadline: newDeadline } : t)));
      const label = { today: "Today", tomorrow: "Tomorrow", week: "This Week", pending: "Still Pending" }[bucketKey];
      toast.success(`Moved to ${label}`);
    } catch (e2) {
      toast.error("Failed to reschedule");
    }
  };

  // ---- Shared "drop task on a calendar/grid cell" handler (Calendar + Weekly Grid) ----
  // Signature: (taskId, newDate, newOwnerId?) — CalendarView omits newOwnerId,
  // WeeklyGridView passes the row's user so cross-row drops also transfer the
  // task to that assignee. Deadline is normalised to end-of-day IST (23:59)
  // now that we no longer expose a time picker on the task dialog.
  const rescheduleTaskToDate = useCallback(async (taskId, date, newOwnerId) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const newDeadline = `${iso}T23:59:00`;

    const patch = {};
    const dateChanged = (task.deadline || "").slice(0, 10) !== iso;
    if (dateChanged) patch.deadline = newDeadline;

    // Reassignment path — only when Weekly Grid drops on a different row.
    // Full transfer: replace the assignee list with just the new owner so
    // downstream views (Reports, My Tasks) reflect the intended hand-off.
    const ownerChanged = !!newOwnerId && newOwnerId !== task.assigned_to;
    if (ownerChanged) {
      patch.assigned_to = newOwnerId;
      patch.assignees = [newOwnerId];
    }
    if (Object.keys(patch).length === 0) return;

    const prev = tasks;
    setTasks((list) => list.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
    try {
      const { data } = await api.patch(`/tasks/${taskId}`, patch);
      setTasks((list) => list.map((t) => (t.id === taskId ? data : t)));
      if (ownerChanged && dateChanged) toast.success(`Moved to ${iso} & reassigned`);
      else if (ownerChanged) toast.success("Reassigned");
      else toast.success(`Moved to ${iso}`);
    } catch (e) {
      setTasks(prev);
      toast.error("Reschedule failed — reverted");
    }
  }, [tasks]);

  // ---- Filters (Kanban / list views) ----
  const [filterTeam, setFilterTeam] = useState("ALL");
  const [filterMember, setFilterMember] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterMineOnly, setFilterMineOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const teamsInScope = useMemo(() => {
    const set = new Set(users.map((u) => u.team).filter(Boolean));
    return Array.from(set).sort();
  }, [users]);
  const membersInScope = useMemo(() => {
    if (filterTeam === "ALL") return users;
    return users.filter((u) => u.team === filterTeam);
  }, [users, filterTeam]);

  // For editors, only own tasks; managers see all
  // Effective status for the status filter — mirrors the per-assignee bucketing
  // used in Kanban / Weekly Grid so a task the viewer (or the filtered member)
  // just marked "complete" on their own slot doesn't get dropped from a
  // status=Complete filter because a co-assignee is still working.
  const effectiveStatus = useCallback((t) => {
    const isMulti = (t.assignees || []).length > 1;
    if (!isMulti) return t.status;
    const scopeUid = filterMember !== "ALL" ? filterMember : user?.id;
    if (scopeUid) {
      const slot = (t.assignee_details || []).find((a) => a.user_id === scopeUid);
      if (slot && slot.status) return slot.status;
    }
    return t.status;
  }, [filterMember, user]);

  // Fast lookup: which team is each user on? Rebuilt whenever the roster
  // changes. Used by the team filter below so a task assigned to a Digital
  // Team member surfaces when filtering "Digital Team", even if the task's
  // own `team` field is blank/stale (belt + braces for the prod drift bug).
  const teamByUserId = useMemo(() => {
    const map = new Map();
    users.forEach((u) => { if (u.id && u.team) map.set(u.id, u.team); });
    return map;
  }, [users]);

  const baseVisible = isManager ? tasks : tasks.filter((t) => t.assigned_to === user?.id || (t.assignees || []).includes(user?.id));
  const visibleTasks = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return baseVisible.filter((t) => {
      if (filterMineOnly && !(t.assigned_to === user?.id || (t.assignees || []).includes(user?.id))) return false;
      if (filterTeam !== "ALL") {
        // Match on either the task's team field OR any assignee's current
        // team — so re-assignments and stale rows never hide a task from
        // the correct manager. This mirrors the backend's manager query.
        const assignees = (t.assignees && t.assignees.length ? t.assignees : [t.assigned_to]).filter(Boolean);
        const assigneeTeams = assignees.map((uid) => teamByUserId.get(uid)).filter(Boolean);
        if (t.team !== filterTeam && !assigneeTeams.includes(filterTeam)) return false;
      }
      if (filterMember !== "ALL" && !(t.assigned_to === filterMember || (t.assignees || []).includes(filterMember))) return false;
      if (filterStatus !== "ALL" && effectiveStatus(t) !== filterStatus) return false;
      if (q) {
        // Full-text search across every field the user might type — title, project,
        // client, description, hold reason, and assignee names.
        const hay = [
          t.title, t.client, t.description, t.hold_reason, t.incomplete_reason,
          ...(Object.values(t.assignee_names || {})),
          t.assigned_to_name,
        ].filter(Boolean).join(" \u0001 ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [baseVisible, filterTeam, filterMember, filterStatus, filterMineOnly, user, searchQuery, effectiveStatus, teamByUserId]);
  const rows = isManager ? users : users.filter((u) => u.id === user?.id);

  // Rows visible on the Weekly Grid — respect the filter bar the same way the
  // "By Editor" view hides members that don't match. Team, member and My-tasks
  // filters gate which rows render; status/search filter the chips inside them
  // (already applied through `visibleTasks`).
  const weeklyGridRows = useMemo(() => {
    let list = rows;
    // Row filter: only users whose HOME team matches the selected filter.
    // (We used to also include users receiving cross-team tasks, but that
    // surfaced empty ghost rows — e.g. Arnav/Mrunali on the Edit-Team view.
    // Task-level rescue is handled by visibleTasks below, which lets a
    // manager see a cross-team task inside the correct assignee's home row.)
    if (filterTeam !== "ALL") list = list.filter((u) => u.team === filterTeam);
    if (filterMember !== "ALL") list = list.filter((u) => u.id === filterMember);
    if (filterMineOnly) list = list.filter((u) => u.id === user?.id);
    return list;
  }, [rows, filterTeam, filterMember, filterMineOnly, user]);

  // Fan tasks out by assignee (supports the multi-assignee model) and — crucially —
  // source from `visibleTasks` so the filter bar drives the "By Editor" view too.
  const tasksByUser = useMemo(() => {
    const map = new Map(users.map((u) => [u.id, []]));
    visibleTasks.forEach((t) => {
      const assigneeIds = (t.assignees && t.assignees.length ? t.assignees : [t.assigned_to]).filter(Boolean);
      const seen = new Set();
      assigneeIds.forEach((uid) => {
        if (seen.has(uid)) return;
        seen.add(uid);
        if (!map.has(uid)) map.set(uid, []);
        map.get(uid).push(t);
      });
    });
    return map;
  }, [users, visibleTasks]);

  // For each user, split their filtered tasks into { today, tomorrow, week, pending }
  const bucketedByUser = useMemo(() => {
    const now = new Date();
    const map = new Map();
    tasksByUser.forEach((uTasks, uid) => {
      const buckets = { today: [], tomorrow: [], week: [], pending: [] };
      uTasks.forEach((t) => {
        // A task can now surface in multiple buckets (e.g. a task starting
        // today, due tomorrow shows in both TODAY and TOMORROW columns).
        bucketsOf(t, now, uid).forEach((b) => buckets[b].push(t));
      });
      const sorter = (a, b) => {
        if (priorityRank(a) !== priorityRank(b)) return priorityRank(a) - priorityRank(b);
        const ad = a.deadline ? new Date(a.deadline).getTime() : Infinity;
        const bd = b.deadline ? new Date(b.deadline).getTime() : Infinity;
        return ad - bd;
      };
      Object.values(buckets).forEach((arr) => arr.sort(sorter));
      map.set(uid, buckets);
    });
    return map;
  }, [tasksByUser]);


  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2"><LayoutGrid size={12} /> Whiteboard</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1">
            {isManager ? "The team board" : "Your board"}
          </h1>
          <p className="text-sm text-white/50 mt-1">
            {isManager
              ? "Every editor. Every task. Every deadline."
              : "Your active tasks — pick a view and get shipping."}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-white/10 bg-surface p-1">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                data-testid={`view-toggle-${v.key}`}
                onClick={() => setView(v.key)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors ${
                  view === v.key ? "bg-[#E11D2E] text-white" : "text-white/60 hover:text-white"
                }`}
              >
                <v.icon size={12} /> {v.label}
              </button>
            ))}
          </div>

          <Button
            variant="ghost" size="sm" onClick={load}
            className="text-white/60 hover:text-white hover:bg-white/5"
          >
            <RefreshCw size={14} className="mr-2" /> Refresh
          </Button>
          {view === "weekly-grid" && user?.is_super_admin && (
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm("Send Weekly + Social-Calendar submission tasks to every manager now? (Automatically fired every Monday 09:00 IST — use this for testing or catch-up.)")) return;
                try {
                  const { data } = await api.post("/admin/weekly-submissions/trigger");
                  toast.success(`${data.total} submission task(s) created.`);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Failed to trigger");
                }
              }}
              className="border-amber-500/40 text-amber-200 hover:border-amber-400 hover:text-white hover:bg-amber-500/10"
              data-testid="trigger-weekly-submissions-btn"
            >
              <CalendarClock size={13} className="mr-1.5" /> Trigger Submissions Now
            </Button>
          )}
          {(user?.is_super_admin || user?.is_admin) && (
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm("Roll every unfinished (in-progress / not-started) task with a past deadline onto today?\n\nOn-hold tasks are NOT rolled — they stay on their original date until someone takes them off hold.\n\nAutomatically fires every day at 05:30 IST — use this to catch up now if the server was down overnight.")) return;
                try {
                  const { data } = await api.post("/admin/rollover-now");
                  toast.success(`${data.moved} task(s) rolled onto today.`);
                  await load();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Failed to trigger rollover");
                }
              }}
              className="border-sky-500/40 text-sky-200 hover:border-sky-400 hover:text-white hover:bg-sky-500/10"
              data-testid="trigger-rollover-btn"
              title="Move stale tasks onto today (auto-runs 05:30 daily)"
            >
              <RefreshCw size={13} className="mr-1.5" /> Roll stale onto today
            </Button>
          )}
          {isManager && (
            <Button
              data-testid={TID.addTaskBtn}
              onClick={() => openNew(null)}
              className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full"
            >
              <Plus size={16} className="mr-1" /> Assign Task
            </Button>
          )}
        </div>
      </div>

      {/* Global filter bar — applies to By-Editor / Kanban / Calendar */}
      {!loading && (
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs" data-testid="whiteboard-filter-bar">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search title, client, notes, assignee…"
              data-testid="whiteboard-search"
              className="w-full h-9 bg-black/40 border border-white/10 rounded-full pl-9 pr-8 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-red-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                data-testid="whiteboard-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {isManager && (
            <select
              data-testid="whiteboard-filter-team"
              value={filterTeam}
              onChange={(e) => { setFilterTeam(e.target.value); setFilterMember("ALL"); }}
              className="bg-black/40 border border-white/10 rounded-md px-3 py-1.5 text-white/80 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="ALL">All teams</option>
              {teamsInScope.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          {isManager && (
            <select
              data-testid="whiteboard-filter-member"
              value={filterMember}
              onChange={(e) => setFilterMember(e.target.value)}
              className="bg-black/40 border border-white/10 rounded-md px-3 py-1.5 text-white/80 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <option value="ALL">All members</option>
              {membersInScope.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <select
            data-testid="whiteboard-filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-black/40 border border-white/10 rounded-md px-3 py-1.5 text-white/80 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            <option value="ALL">All statuses</option>
            <option value="not_started">Not Started</option>
            <option value="in_progress">In Progress</option>
            <option value="carry_forward">Carry Forward</option>
            <option value="on_hold">On Hold</option>
            <option value="complete">Complete</option>
            <option value="incomplete">Incomplete</option>
          </select>
          {isManager && (
            <button
              type="button"
              data-testid="whiteboard-filter-mine"
              onClick={() => setFilterMineOnly((v) => !v)}
              className={`px-3 py-1.5 rounded-md border text-xs uppercase tracking-widest ${
                filterMineOnly ? "bg-[#E11D2E]/20 border-[#E11D2E]/40 text-[#E11D2E]" : "border-white/10 text-white/60 hover:text-white"
              }`}
            >
              My tasks only
            </button>
          )}
          {(filterTeam !== "ALL" || filterMember !== "ALL" || filterStatus !== "ALL" || filterMineOnly) && (
            <button
              type="button"
              data-testid="whiteboard-filter-reset"
              onClick={() => { setFilterTeam("ALL"); setFilterMember("ALL"); setFilterStatus("ALL"); setFilterMineOnly(false); }}
              className="text-xs text-white/50 hover:text-white underline underline-offset-2"
            >
              Clear filters
            </button>
          )}
          <span className="ml-auto text-xs text-white/40 font-mono">{visibleTasks.length} task{visibleTasks.length === 1 ? "" : "s"}</span>
        </div>
      )}

      {loading && <div className="text-white/40 text-sm">Loading whiteboard…</div>}
      {loading ? null : view === "board" ? (
        <>
          <TeamStatusStrip />
          <div className="grid gap-4">
          {rows.map((u) => {
            const uTasks = tasksByUser.get(u.id) || [];
            const buckets = bucketedByUser.get(u.id) || { today: [], tomorrow: [], week: [], pending: [] };
            // Dedupe across buckets — a spanning task is intentionally shown in
            // multiple columns, but should count once in the summary chip.
            const openCount = new Set([
              ...buckets.today.map((t) => t.id),
              ...buckets.tomorrow.map((t) => t.id),
              ...buckets.week.map((t) => t.id),
              ...buckets.pending.map((t) => t.id),
            ]).size;
            // Respect the member filter first — if the manager picked a specific
            // person we must NEVER render anyone else's row, even if they share a
            // multi-assignee task with the selected one.
            if (filterMember !== "ALL" && u.id !== filterMember) return null;
            if (filterTeam !== "ALL" && u.team !== filterTeam) return null;
            if (filterMineOnly && u.id !== user?.id) return null;
            // If any filter is active and this member has zero matching tasks, hide the row entirely
            // (otherwise a narrow filter still renders a wall of empty member cards).
            const anyFilterActive = filterTeam !== "ALL" || filterMember !== "ALL" || filterStatus !== "ALL" || filterMineOnly;
            if (anyFilterActive && uTasks.length === 0 && u.id !== user?.id) return null;
            return (
              <div
                key={u.id}
                className="rounded-xl border border-white/8 bg-surface overflow-hidden animate-fade-in-up"
              >
                <div className="flex items-center justify-between px-4 py-3 bg-surface border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <Avatar user={u} size={36} />
                    <div>
                      <div className="text-sm font-medium flex items-center gap-2">
                        {u.name}
                        {u.title && (
                          <span
                            className={`text-[0.65rem] uppercase tracking-widest rounded px-1.5 py-0.5 border ${
                              u.role === "manager"
                                ? "text-[#E11D2E] bg-[#E11D2E]/10 border-[#E11D2E]/30"
                                : "text-white/70 bg-white/5 border-white/10"
                            }`}
                          >
                            {u.title}
                          </span>
                        )}
                        {(leaves.by_user[u.id] || []).includes(todayIso) && (
                          <span
                            data-testid={`on-leave-pill-${u.id}`}
                            title="On leave today"
                            className="text-[0.65rem] uppercase tracking-widest rounded px-1.5 py-0.5 border text-amber-300 bg-amber-500/10 border-amber-500/40 inline-flex items-center gap-1"
                          ><Coffee size={10} /> On leave</span>
                        )}
                      </div>
                      <div className="text-xs text-white/40 font-mono">
                        {openCount} open · {uTasks.length - openCount} done · {u.team}
                      </div>
                    </div>
                  </div>
                  {isManager && (
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => openNew(u.id)}
                      className="text-white/60 hover:text-white hover:bg-white/5"
                    >
                      <Plus size={14} className="mr-1" /> Task
                    </Button>
                  )}
                </div>

                {uTasks.length === 0 ? (
                  <div className="p-4">
                    <EmptyState
                      text="No tasks assigned yet."
                      actionLabel="Assign a task"
                      onAction={isManager ? () => openNew(u.id) : null}
                      testid={`empty-user-${u.id}`}
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-0 divide-y md:divide-y-0 md:divide-x divide-white/5">
                    {BUCKETS.map((b) => {
                      const list = buckets[b.key];
                      const isDropTarget = dragTarget === `${u.id}:${b.key}`;
                      const isDropForbidden = dragging && dragging.ownerId !== u.id;
                      return (
                        <div
                          key={b.key}
                          data-testid={`bucket-${u.id}-${b.key}`}
                          onDragOver={(e) => onBucketDragOver(e, u.id, b.key)}
                          onDragLeave={() => setDragTarget((cur) => (cur === `${u.id}:${b.key}` ? null : cur))}
                          onDrop={(e) => onBucketDrop(e, u.id, b.key)}
                          className={`p-3 flex flex-col min-h-[120px] transition-colors ${
                            isDropTarget ? "bg-white/[0.04] ring-1 ring-inset ring-[#E11D2E]/50" :
                            isDropForbidden ? "opacity-50" : ""
                          }`}
                        >
                          <div className={`flex items-center justify-between mb-2 pb-1.5 border-b ${b.tone}`}>
                            <div className={`flex items-center gap-1.5 text-[0.7rem] uppercase tracking-widest font-medium ${b.tone.split(" ")[0]}`}>
                              <b.icon size={11} /> {b.label}
                            </div>
                            <span className={`text-[0.7rem] font-mono ${list.length ? b.tone.split(" ")[0] : "text-white/30"}`}>
                              {list.length}
                            </span>
                          </div>
                          {list.length === 0 ? (
                            isDropTarget ? (
                              <div className="text-xs mt-1 flex-1 grid place-items-center text-white/60">Drop to move here</div>
                            ) : (
                              <EmptyState
                                compact
                                text={b.key === "today" ? "Nothing due today" : b.key === "tomorrow" ? "Nothing due tomorrow" : b.key === "week" ? "Clear for the week" : "Nothing pending"}
                                actionLabel="Add task"
                                onAction={isManager && b.key !== "pending" ? () => openNew(u.id) : null}
                                testid={`empty-bucket-${u.id}-${b.key}`}
                              />
                            )
                          ) : (
                            <div className="space-y-2">
                              {list.map((t) => {
                                const movable = canMoveTask(t);
                                return (
                                  <div
                                    key={t.id}
                                    draggable={movable}
                                    onDragStart={(e) => onCardDragStart(e, t)}
                                    onDragEnd={onCardDragEnd}
                                    className={`${movable ? "cursor-grab active:cursor-grabbing" : ""} ${dragging?.taskId === t.id ? "opacity-40" : ""}`}
                                  >
                                    <TaskCard
                                      task={t}
                                      currentUser={user}
                                      users={users}
                                      isManager={isManager}
                                      onEdit={openEdit}
                                      onDelete={isManager ? delTask : null}
                                      onTogglePriority={isManager ? togglePriority : null}
                                      onStatusChange={updateStatus}
                                      assigneeOnLeaveToday={(leaves.by_user[t.assigned_to] || []).includes(todayIso)}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      ) : view === "list" ? (
        <ListView
          tasks={visibleTasks}
          users={users}
          currentUser={user}
          isManager={isManager}
          onEdit={openEdit}
          onStatusChange={updateStatus}
          onTogglePriority={isManager ? togglePriority : null}
          onAdd={isManager ? () => openNew(null) : null}
        />
      ) : view === "kanban" ? (
        <>
          <KanbanView
            tasks={visibleTasks}
            users={users}
            isManager={isManager}
            currentUserId={user?.id}
            onEdit={openEdit}
            onDelete={delTask}
            onTogglePriority={togglePriority}
            onTasksUpdated={setTasks}
            onStatusChange={updateStatus}
            leavesByUser={leaves.by_user}
            todayIso={todayIso}
          />
        </>
      ) : view === "calendar" ? (
        <CalendarView
          tasks={visibleTasks}
          users={users}
          currentUserId={user?.id}
          onEdit={openEdit}
          canAdd={isManager}
          onStatusChange={updateStatus}
          leavesByUser={leaves.by_user}
          onAddOnDate={(date) => {
            const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
            setEditing(null);
            setDefaultAssignee(null);
            setPrefillDate(iso);
            setDialogOpen(true);
          }}
          onDropTask={rescheduleTaskToDate}
        />
      ) : (
        <>
          <WeeklyGridView
          users={weeklyGridRows}
          tasks={visibleTasks}
          currentUserId={user?.id}
          isManager={isManager}
          onEdit={openEdit}
          onStatusChange={updateStatus}
          leavesByUser={leaves.by_user}
          backlog={isManager || user?.is_admin ? { team: filterTeam, users, currentUser: user, onTaskCreated: () => load(true) } : null}
          onAddOnDateForUser={(date, uid) => {
            const iso = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
            setEditing(null);
            setDefaultAssignee(uid);
            setPrefillDate(iso);
            setDialogOpen(true);
          }}
          onDropTask={(taskId, date) => rescheduleTaskToDate(taskId, date)}
        />
        </>
      )}

      <TaskDialog
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) setPrefillDate(null); }}
        users={users}
        existing={editing}
        defaultAssignee={defaultAssignee}
        defaultStartDate={prefillDate}
        defaultDeadlineDate={prefillDate}
        onSaved={load}
        onTaskCompleted={(t) => setCompletePopupTask(t)}
      />
      <CompleteLinksDialog
        open={!!completePopupTask}
        task={completePopupTask}
        onClose={(updated) => {
          if (updated) setTasks((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          setCompletePopupTask(null);
        }}
      />
    </div>
  );
}
